/**
 * Controls: the button in three weights (as a button, a router link, or an
 * icon-only button) and the labelled form field with its hint or error.
 * Re-exported through `./primitives.js`.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/* ---- Buttons --------------------------------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'quiet';

function buttonClass(
  variant: ButtonVariant,
  small: boolean,
  className: string,
): string {
  const weight =
    variant === 'primary'
      ? 'mj-ws-btn-p'
      : variant === 'secondary'
        ? 'mj-ws-btn-s'
        : 'mj-ws-btn-q';
  return `mj-ws-btn ${weight}${small ? ' mj-ws-btn-sm' : ''} ${className}`.trim();
}

export function Button({
  variant = 'secondary',
  small = false,
  className = '',
  type = 'button',
  children,
  ...rest
}: {
  readonly variant?: ButtonVariant;
  readonly small?: boolean;
  readonly className?: string;
  readonly type?: 'button' | 'submit';
  readonly children: ReactNode;
  readonly disabled?: boolean | undefined;
  readonly onClick?: (() => void) | undefined;
  readonly 'aria-pressed'?: boolean | undefined;
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-disabled'?: boolean | undefined;
  readonly title?: string | undefined;
  readonly id?: string | undefined;
  readonly autoFocus?: boolean | undefined;
}): ReactNode {
  return (
    <button type={type} className={buttonClass(variant, small, className)} {...rest}>
      {children}
    </button>
  );
}

export function ButtonLink({
  to,
  variant = 'secondary',
  small = false,
  className = '',
  children,
  ...rest
}: {
  readonly to: string;
  readonly variant?: ButtonVariant;
  readonly small?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly 'aria-label'?: string;
}): ReactNode {
  return (
    <Link to={to} className={buttonClass(variant, small, className)} {...rest}>
      {children}
    </Link>
  );
}

export function IconButton({
  label,
  children,
  className = '',
  ...rest
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly 'aria-expanded'?: boolean;
  readonly 'aria-haspopup'?: 'menu' | 'dialog';
  readonly 'aria-controls'?: string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      className={`mj-ws-icb ${className}`.trim()}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---- Forms ----------------------------------------------------------- */

export function Field({
  label,
  id,
  hint,
  error,
  optional,
  children,
}: {
  readonly label: ReactNode;
  readonly id: string;
  readonly hint?: ReactNode;
  readonly error?: string | null;
  readonly optional?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="mj-ws-field">
      <label className="mj-ws-lab" htmlFor={id}>
        {label}{' '}
        {optional !== undefined ? (
          <span className="mj-ws-muted">{optional}</span>
        ) : null}
      </label>
      {children}
      {error ? (
        <div id={`${id}-err`} className="mj-ws-err" role="alert">
          {error}
        </div>
      ) : hint !== undefined ? (
        <div id={`${id}-hint`} className="mj-ws-muted mj-ws-xs">
          {hint}
        </div>
      ) : null}
    </div>
  );
}
